// 统一 API 客户端：/api 前缀、JSON 编解码、错误转 ApiClientError、
// cookie 写请求自动带 x-csrf-token（token 由 /api/auth/me 下发并缓存在这里）、
// 401 跳登录页。页面与 patterns 一律走这里发请求，禁止裸 fetch。

export interface ApiErrorShape {
  error: string;
  message?: string;
  requestId?: string | null;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;

  constructor(status: number, code: string, message: string, requestId: string | null = null) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

export function isApiClientError(err: unknown): err is ApiClientError {
  return err instanceof ApiClientError;
}

let csrfToken: string | null = null;

/** 登录后由 AuthProvider 用 /api/auth/me 的 csrfToken 调用 */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** 401 跳转钩子：测试环境可替换；默认跳 /login 并带 redirect 参数 */
export let onUnauthorized: () => void = () => {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/login")) return;
  const redirect = window.location.pathname + window.location.search;
  window.location.assign(`/login?redirect=${encodeURIComponent(redirect)}`);
};

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export interface RequestOptions {
  /** 查询参数（undefined 值自动跳过） */
  query?: Record<string, string | number | boolean | undefined>;
  /** 跳过 401 自动跳登录（AuthProvider 探测会话时用） */
  skipAuthRedirect?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = path.startsWith("/api") ? path : `/api${path.startsWith("/") ? "" : "/"}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseError(res: Response): Promise<ApiClientError> {
  let body: ApiErrorShape | null = null;
  try {
    body = (await res.json()) as ApiErrorShape;
  } catch {
    // 非 JSON 错误体（网关/代理错误等），忽略
  }
  const code = body?.error || `http_${res.status}`;
  const message = body?.message || `请求失败（HTTP ${res.status}）`;
  return new ApiClientError(res.status, code, message, body?.requestId ?? null);
}

async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (!SAFE_METHODS.has(method) && csrfToken) headers["x-csrf-token"] = csrfToken;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "same-origin",
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiClientError(0, "network_error", "网络异常，请检查连接后重试");
  }

  if (res.status === 401 && !opts.skipAuthRedirect) {
    setCsrfToken(null);
    onUnauthorized();
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("POST", path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PUT", path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PATCH", path, body, opts),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, undefined, opts),
};
